import { formatLocalDate, parseDate } from "../utils/DateUtil";

import type { OrderSource as OrderSourceType, InitialEmployeeCheckoutHeader, PaymentType } from "../model/CheckoutData";
import type { Country, UserId, StoreId, CardholderNumber, Money } from "./BaseTypes";
import type { EmployeeCommentType } from "./checkout/OrderCommentsStore";

export type RealPaymentType = {
	paymentAllowance: string;
	paymentType: string | null | undefined;
	isPrimaryPayment: boolean;
	paymentId: number | null | undefined;
};
export type OrderHeader = EmployeeOrderHeader | CustomerOrderHeader;
export type PaymentPreauthorization = {
	forwardUrl: string;
};
export type PaymentPreauthorizationRequestData = {
	checkoutId: string;
	description: string;
	amount: {
		total: number;
		shipping: number;
		tax: number | null | undefined;
	};
	currency: string;
	customerId: UserId;
	additionalId: string;
	baseReturnUrl: string;
	baseErrorUrl: string;
	country: Country;
	language: string;
	storeId: StoreId;
	multiUse: boolean;
	city: string | null | undefined;
	zipcode: string | null | undefined;
	streetaddress: string | null | undefined;
};
// ---------------------------------------------------------------------------
export class CreditCheckInfo {
	limit: Money | null | undefined;
	usedLimit: Money | null | undefined;
	timestampLastReceive: number | null | undefined;
}
export class OrderSource {
	name: OrderSourceType;
	i18nKey: string;

	constructor(name: OrderSourceType) {
		this.name = name;
		this.i18nKey = "ORC_ORDERSOURCE_" + name;
	}

	static PHONE_INCOMING_INT: OrderSource = new OrderSource("PHONE_INCOMING_INT");
	static PHONE_INCOMING_EXT: OrderSource = new OrderSource("PHONE_INCOMING_EXT");
	static PHONE_OUTBOUND_INT: OrderSource = new OrderSource("PHONE_OUTBOUND_INT");
	static PHONE_OUTBOUND_EXT: OrderSource = new OrderSource("PHONE_OUTBOUND_EXT");
	static EMAIL: OrderSource = new OrderSource("EMAIL");
	static EMAIL_CALL_CENTER: OrderSource = new OrderSource("EMAIL_CALL_CENTER");
	static FAX_STORE: OrderSource = new OrderSource("FAX_STORE");
	static FAX_CALL_CENTER: OrderSource = new OrderSource("FAX_CALL_CENTER");
	static CUSTOMERVISIT: OrderSource = new OrderSource("CUSTOMERVISIT");
	static DRIVER: OrderSource = new OrderSource("DRIVER");
	static STOREVISIT: OrderSource = new OrderSource("STOREVISIT");
	static WEBSHOP_INT: OrderSource = new OrderSource("WEBSHOP_INT");
	static WEBSHOP_EXT: OrderSource = new OrderSource("WEBSHOP_EXT");
	static CUSTOMER: OrderSource = new OrderSource("CUSTOMER");
	static WE_CHAT: OrderSource = new OrderSource("WE_CHAT");
	static FOLLOW_UP_ORDER: OrderSource = new OrderSource("FOLLOW_UP_ORDER");
	static values: Array<OrderSource> = [
		OrderSource.PHONE_INCOMING_INT,
		OrderSource.PHONE_INCOMING_EXT,
		OrderSource.PHONE_OUTBOUND_INT,
		OrderSource.PHONE_OUTBOUND_EXT,
		OrderSource.EMAIL,
		OrderSource.EMAIL_CALL_CENTER,
		OrderSource.FAX_STORE,
		OrderSource.FAX_CALL_CENTER,
		OrderSource.CUSTOMERVISIT,
		OrderSource.DRIVER,
		OrderSource.STOREVISIT,
		OrderSource.WEBSHOP_INT,
		OrderSource.WEBSHOP_EXT,
		OrderSource.CUSTOMER,
		OrderSource.WE_CHAT,
		OrderSource.FOLLOW_UP_ORDER,
	];
	static valuesForCustomers: Array<OrderSource> = [OrderSource.CUSTOMER];
	static valuesForEmployees: Array<OrderSource> = [
		OrderSource.PHONE_INCOMING_INT,
		OrderSource.PHONE_INCOMING_EXT,
		OrderSource.PHONE_OUTBOUND_INT,
		OrderSource.PHONE_OUTBOUND_EXT,
		OrderSource.EMAIL,
		OrderSource.EMAIL_CALL_CENTER,
		OrderSource.FAX_STORE,
		OrderSource.FAX_CALL_CENTER,
		OrderSource.CUSTOMERVISIT,
		OrderSource.DRIVER,
		OrderSource.STOREVISIT,
		OrderSource.WEBSHOP_INT,
		OrderSource.WEBSHOP_EXT,
		OrderSource.WE_CHAT,
		OrderSource.FOLLOW_UP_ORDER,
	];

	static valueOf(name: string): OrderSource {
		return ORDER_SOURCE_VALUES_BY_NAME[name];
	}
}

const ORDER_SOURCE_VALUES_BY_NAME = OrderSource.values.reduce(
	(acc, orderSource) => ({ ...acc, [orderSource.name]: orderSource }),
	{} as Record<string, OrderSource>,
);

export class CustomerDataAdditions {
	pickingComment: string;
	deliveryComment: string;
	invoiceComment: string;

	constructor() {
		this.pickingComment = "";
		this.deliveryComment = "";
		this.invoiceComment = "";
	}

	toJson(): Record<string, any> {
		const json = {} as any;
		json.pickingComment = this.pickingComment;
		json.deliveryComment = this.deliveryComment;
		json.invoiceComment = this.invoiceComment;
		return json;
	}

	static fromJson(json: Record<string, any>): CustomerDataAdditions {
		const data = new CustomerDataAdditions();
		data.pickingComment = json.pickingComment;
		data.deliveryComment = json.deliveryComment;
		data.invoiceComment = json.invoiceComment;
		return data;
	}

	static fromEmployeeComments(
		comments: Partial<Record<EmployeeCommentType, string | null | undefined>>,
	): CustomerDataAdditions {
		const data = new CustomerDataAdditions();
		data.pickingComment = comments.picking ?? "";
		data.deliveryComment = comments.delivery ?? "";
		data.invoiceComment = comments.invoice ?? "";
		return data;
	}
}
export class BaseOrderHeader {
	deliveryDate: Date | null | undefined;
	deliveryAddressId: string | null | undefined;
	realPaymentType: PaymentType | null | undefined;
	orderSource: OrderSource | null | undefined;
	ownOrderNumber: string;
	ownStoreNumber: string;
	cardholderNumber: CardholderNumber | null | undefined;
	shoppingCartComment: string | null | undefined;
	sendOrderConfirmationToCustomer: boolean;

	constructor() {
		this.deliveryDate = null;
		this.deliveryAddressId = null;
		this.realPaymentType = null;
		this.orderSource = null;
		this.ownOrderNumber = "";
		this.ownStoreNumber = "";
		this.cardholderNumber = null;
		this.shoppingCartComment = "";
		this.sendOrderConfirmationToCustomer = false;
	}

	isReady(): boolean {
		throw new TypeError("Trying to call abstract method 'isReady' of class BaseOrderHeader.");
	}

	toJson(): Record<string, any> {
		throw new TypeError("Trying to call abstract method 'toJson' of class BaseOrderHeader.");
	}
}

function formatDate(date: Date): string {
	return formatLocalDate(date, "yyyy-MM-dd");
}

export class CustomerOrderHeader extends BaseOrderHeader {
	headerType: "CUSTOMER";

	constructor() {
		super();
		this.headerType = "CUSTOMER";
	}

	isReady(): boolean {
		return !!(this.deliveryDate && this.realPaymentType && this.orderSource);
	}

	toJson(): Record<string, any> {
		const json = {} as any;
		json.deliveryDate = this.deliveryDate ? formatDate(this.deliveryDate) : null;
		json.deliveryAddressId = this.deliveryAddressId;
		json.orderSource = this.orderSource ? this.orderSource.name : null;
		json.ownOrderNumber = this.ownOrderNumber;
		json.ownStoreNumber = this.ownStoreNumber;
		json.cardholderNumber = this.cardholderNumber;
		json.sendOrderConfirmationToCustomer = this.sendOrderConfirmationToCustomer;
		json.realPaymentType = PaymentTypeToJson(this.realPaymentType);
		json.comment = this.shoppingCartComment;
		return json;
	}

	static fromJson(json: Record<string, any>): CustomerOrderHeader {
		// needed because support for "Sugar-free classes" is limited and misses this function in prototype
		const orderHeader: CustomerOrderHeader = new CustomerOrderHeader() as any;
		CustomerOrderHeader.parseJsonInto(json, orderHeader);
		return orderHeader;
	}

	static parseJsonInto(json: Record<string, any>, orderHeader: Record<string, any>): void {
		orderHeader.cardholderNumber = json.cardholderNumber;
		orderHeader.deliveryDate = json.deliveryDate && parseDate(json.deliveryDate);
		orderHeader.deliveryAddressId = json.deliveryAddressId;
		orderHeader.realPaymentType = parseRealPaymentType(json.realPaymentType);
		orderHeader.orderSource = OrderSource.valueOf(json.orderSource);
		orderHeader.ownOrderNumber = json.ownOrderNumber;
		orderHeader.ownStoreNumber = json.ownStoreNumber;
		orderHeader.shoppingCartComment = json.comment;
		orderHeader.sendOrderConfirmationToCustomer = json.sendOrderConfirmationToCustomer;
	}
}
export class EmployeeOrderHeader extends BaseOrderHeader {
	headerType: "EMPLOYEE";
	singleOrder: boolean;
	customerDataAdditions: CustomerDataAdditions;
	sendOrderConfirmationToCardholder: boolean;
	ignoreFees: boolean;
	sendPickingListToCentralWarehouse: boolean;
	applyEgalisationTax: boolean;
	fromJsonEmployee: ((json: Record<string, any>) => EmployeeOrderHeader) | undefined;

	constructor() {
		super();
		this.headerType = "EMPLOYEE";
		this.singleOrder = false;
		this.customerDataAdditions = new CustomerDataAdditions();
		this.sendOrderConfirmationToCardholder = false;
		this.ignoreFees = false;
		this.sendPickingListToCentralWarehouse = true;
		this.applyEgalisationTax = false;
	}

	toJson(): Record<string, any> {
		// we are simulating classes via functors, so, we want call the "super" method, but on this instance
		const json = CustomerOrderHeader.prototype.toJson.call(this);
		json.singleOrder = this.singleOrder;
		json.customerDataAdditions = this.customerDataAdditions ? this.customerDataAdditions.toJson() : null;
		json.sendOrderConfirmationToCardholder = this.sendOrderConfirmationToCardholder;
		json.ignoreFees = this.ignoreFees;
		json.sendPickingListToCentralWarehouse = this.sendPickingListToCentralWarehouse;
		json.applyEgalisationTax = this.applyEgalisationTax;
		return json;
	}

	static fromJsonEmployee(json: Record<string, any>): EmployeeOrderHeader {
		// needed because support for "Sugar-free classes" is limited and misses this function in prototype
		const orderHeader: EmployeeOrderHeader = new EmployeeOrderHeader() as any;
		// we are simulating classes via functors, so, we want call the "super" method, but on this instance
		CustomerOrderHeader.parseJsonInto(json, orderHeader);
		orderHeader.singleOrder = json.singleOrder;
		orderHeader.customerDataAdditions = CustomerDataAdditions.fromJson(json.customerDataAdditions);
		orderHeader.sendOrderConfirmationToCardholder = json.sendOrderConfirmationToCardholder;
		orderHeader.ignoreFees = json.ignoreFees;
		orderHeader.sendPickingListToCentralWarehouse = json.sendPickingListToCentralWarehouse;
		orderHeader.applyEgalisationTax = json.applyEgalisationTax;
		return orderHeader;
	}

	toInitialEmployeeCheckoutHeader(cardholderNumber?: string): InitialEmployeeCheckoutHeader {
		return {
			deliveryDate: this.deliveryDate && formatDate(this.deliveryDate),
			paymentType: this.realPaymentType,
			orderSource: this.orderSource && (this.orderSource.name as any),
			// these are not enum-typed, just strings
			ownOrderNumber: this.ownOrderNumber,
			ownStoreNumber: this.ownStoreNumber,
			cardholderNumber: cardholderNumber || this.cardholderNumber,
			sendOrderConfirmationToCustomer: this.sendOrderConfirmationToCustomer,
			sendOrderConfirmationToCardholder: this.sendOrderConfirmationToCardholder,
			singleOrder: this.singleOrder,
			customerDataAdditions: {
				pickingComment: this.customerDataAdditions.pickingComment,
				deliveryComment: this.customerDataAdditions.deliveryComment,
				invoiceComment: this.customerDataAdditions.invoiceComment,
			},
			ignoreFees: this.ignoreFees,
			sendPickingListToCentralWarehouse: this.sendPickingListToCentralWarehouse,
			applyEgalisationTax: this.applyEgalisationTax,
			cartComment: this.shoppingCartComment,
		};
	}

	isReady(): boolean {
		return !!(this.deliveryDate && this.realPaymentType && this.orderSource);
	}
}
export function PaymentTypeToJson(type: PaymentType | null | undefined): Record<string, any> | null | undefined {
	if (!type) {
		return null;
	}

	const json = {} as any;
	json.paymentAllowance = type.paymentAllowance;
	json.paymentType = type.paymentType;
	return json;
}
export function parseRealPaymentType(json: Record<string, any>): RealPaymentType | null | undefined {
	if (!json) {
		return null;
	}

	return {
		paymentAllowance: json.paymentAllowance,
		paymentType: json.paymentType,
		isPrimaryPayment: json.isPrimaryPayment,
		paymentId: json.paymentId,
	};
}
export function getRemainingCredit(creditCheckInfo: CreditCheckInfo | null | undefined): Money | null | undefined {
	if (!creditCheckInfo) {
		return null;
	}

	const remaining =
		creditCheckInfo.limit && creditCheckInfo.usedLimit
			? creditCheckInfo.limit.amount - creditCheckInfo.usedLimit.amount
			: null;
	const currency = creditCheckInfo.limit && creditCheckInfo.limit.currency;
	return remaining && currency
		? {
				amount: remaining,
				currency: currency,
			}
		: null;
}
